import React, { useState, useEffect, useRef } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useIsAuthenticated } from "@azure/msal-react";
import { useMsalAuth } from "../../hooks/useMsalAuth";
import { SessionTimeoutDialogFluent } from "./SessionTimeoutDialogFluent";
import { logger } from "../../services/logger.service";

interface SessionTimeoutProps {
  isInactivityActive: boolean;
  inactivityLength: number;
  inactivityTimeout: number;
  isSessionExpiryActive: boolean;
  sessionLength: number;
  sessionTimeout: number;
}

let countdownInterval: ReturnType<typeof setInterval> | undefined;
let timeout: ReturnType<typeof setTimeout> | undefined;
let sessionTimeoutId: ReturnType<typeof setTimeout> | undefined;
let sessionCountdownIntervalId: ReturnType<typeof setInterval> | undefined;
let mainSessiontimeoutIntervalId: ReturnType<typeof setTimeout> | undefined;
let refreshTokenIntervalId: ReturnType<typeof setInterval> | undefined;

const SessionTimeout = ({
  isInactivityActive,
  inactivityLength,
  inactivityTimeout,
  isSessionExpiryActive,
  sessionLength,
  sessionTimeout,
}: SessionTimeoutProps): React.ReactElement => {
  const isAuthenticated = useIsAuthenticated();
  const { getAccessToken, logout } = useMsalAuth();
  const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(0);

  const [hasSessionExpired, setHasSessionExpired] = useState(false);
  const [sessiontimeoutModalOpen, setSessionTimeoutModalOpen] = useState(false);
  const [sessiontimeoutCountdown, setSessionTimeoutCountdown] = useState(0);

  // Track last user activity to silently refresh session for active users
  const lastActivityRef = useRef<number>(Date.now());

  const clearSessionTimeout = (): void => {
    if (timeout) clearTimeout(timeout);
  };

  const clearSessionInterval = (): void => {
    if (countdownInterval) clearInterval(countdownInterval);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      setTimeoutModalOpen(false);
      clearSessionInterval();
      clearSessionTimeout();
      await logout();
    } catch (err) {
      logger.error("Logout failed", err);
    }
  };

  const handleContinue = (): void => {
    setTimeoutModalOpen(false);
    clearSessionInterval();
    clearSessionTimeout();
    getAccessToken();
  };

  const onActive = (): void => {
    if (!timeoutModalOpen) {
      clearSessionInterval();
      clearSessionTimeout();
    }
  };

  const onIdle = (): void => {
    const delay = 1000 * 1;
    if (isAuthenticated && !timeoutModalOpen) {
      timeout = setTimeout(() => {
        let countDown = inactivityTimeout * 60;
        setTimeoutModalOpen(true);
        setTimeoutCountdown(countDown);
        countdownInterval = setInterval(() => {
          if (countDown > 0) {
            setTimeoutCountdown(--countDown);
          } else {
            handleLogout();
          }
        }, 1000);
      }, delay);
    }
  };

  const handleSessionLogout = async (): Promise<void> => {
    try {
      setHasSessionExpired(false);
      setSessionTimeoutModalOpen(false);
      setSessionTimeoutCountdown(0);
      clearSession();
      await logout();
    } catch (err) {
      logger.error("Logout failed", err);
    }
  };

  const handleSessionContinue = (): void => {
    setHasSessionExpired(false);
    setSessionTimeoutModalOpen(false);
    setSessionTimeoutCountdown(0);
    clearSession();
    getAccessToken();
  };

  const isUserActive = (): boolean => {
    const ACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    return Date.now() - lastActivityRef.current < ACTIVITY_THRESHOLD;
  };

  const setSessionClearTimeout = (): void => {
    const delay = 1000 * 1;
    if (isAuthenticated) {
      sessionTimeoutId = setTimeout(() => {
        // If user is actively working, silently refresh the session — no dialog needed
        if (isUserActive()) {
          getAccessToken();
          clearSession();
          return;
        }

        // User is idle — show the session expiry dialog
        let countDown = sessionTimeout * 60;
        setHasSessionExpired(true);
        setSessionTimeoutModalOpen(true);
        setSessionTimeoutCountdown(countDown);
        sessionCountdownIntervalId = setInterval(() => {
          if (countDown > 0) {
            setSessionTimeoutCountdown(--countDown);
          } else {
            handleSessionLogout();
          }
        }, 1000);
      }, delay);
    }
  };

  const clearSession = (): void => {
    if (sessionCountdownIntervalId) clearInterval(sessionCountdownIntervalId);
    if (sessionTimeoutId) clearTimeout(sessionTimeoutId);
    if (mainSessiontimeoutIntervalId) clearInterval(mainSessiontimeoutIntervalId);
    mainSessiontimeoutIntervalId = undefined;
    sessionTimeoutId = undefined;
    sessionCountdownIntervalId = undefined;
    sessionClearTimeout();
  };

  const sessionClearTimeout = (): void => {
    if (mainSessiontimeoutIntervalId) {
      clearTimeout(mainSessiontimeoutIntervalId);
    }
    // Settings → Session → "Session expiry" off: nothing is armed, so no expiry
    // dialog and no forced logout at the end of the session length.
    if (!isSessionExpiryActive) {
      mainSessiontimeoutIntervalId = undefined;
      return;
    }
    mainSessiontimeoutIntervalId = setTimeout(
      setSessionClearTimeout,
      (sessionLength - sessionTimeout) * 60 * 1000,
    );
  };

  const getRefreshToken = async (): Promise<void> => {
    try {
      await getAccessToken();
    } catch (error) {
      logger.error("Failed to refresh access token", error);
    }
  };

  const refreshToken = (): void => {
    if (!refreshTokenIntervalId && isAuthenticated) {
      refreshTokenIntervalId = setInterval(getRefreshToken, 50 * 60 * 1000);
      getRefreshToken();
    }
  };

  // Track user activity via DOM events so we know when to silently refresh
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => document.addEventListener(e, handleActivity, { passive: true }));
    return () => {
      events.forEach((e) => document.removeEventListener(e, handleActivity));
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refreshToken();
    }

    return () => {
      if (refreshTokenIntervalId) {
        clearInterval(refreshTokenIntervalId);
        refreshTokenIntervalId = undefined;
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isSessionExpiryActive) {
      setHasSessionExpired(false);
      setSessionTimeoutModalOpen(false);
    }
    sessionClearTimeout();

    return () => {
      clearSession();
    };
  }, [isSessionExpiryActive, sessionLength, sessionTimeout]);

  useIdleTimer({
    onActive,
    onIdle,
    timeout: 1000 * 60 * (inactivityLength - inactivityTimeout),
    debounce: 250,
    disabled: !isInactivityActive,
  });

  return (
    <>
      {isInactivityActive && (
        <SessionTimeoutDialogFluent
          countdown={timeoutCountdown}
          onContinue={handleContinue}
          onLogout={() => handleLogout()}
          open={timeoutModalOpen}
        />
      )}

      {hasSessionExpired && (
        <SessionTimeoutDialogFluent
          countdown={sessiontimeoutCountdown}
          onContinue={handleSessionContinue}
          onLogout={() => handleSessionLogout()}
          open={sessiontimeoutModalOpen}
        />
      )}
    </>
  );
};

export default SessionTimeout;
